import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import ProtectedRoute from './components/common/ProtectedRoute';
import LabRouteGuard from './components/common/LabRouteGuard';
import RoleGuard from './components/common/RoleGuard';
import ConsoleShell from './components/layout/ConsoleShell';
import {
  DashboardSkeleton,
  ModelsSkeleton,
  MonitoringSkeleton,
  TrainingSkeleton,
} from './components/ui/LoadingSkeleton';
import { getConsoleLandingPath } from './navigation/consoleNavigation';
import { labRoutesEnabled } from './config/features';
import {
  consoleRoutePaths,
  consoleRoutePatterns,
} from './navigation/consoleRoutePaths';
import { useAuthStore } from './stores/authStore';

const Login = lazy(() => import('./pages/Login'));
const Control = lazy(() => import('./pages/Control'));
const DeviceWorkspace = lazy(() => import('./pages/DeviceWorkspace'));
const Onboard = lazy(() => import('./pages/Onboard'));
const BrowserMicStream = lazy(() => import('./pages/BrowserMicStream'));
const ResetUsb = lazy(() => import('./pages/ResetUsb'));
const AdvancedFileDetection = lazy(() => import('./pages/advanced/FileDetection'));
const Models = lazy(() => import('./pages/Models'));
const Training = lazy(() => import('./pages/Training'));
const TrainingSummary = lazy(() => import('./pages/TrainingSummary'));
const Users = lazy(() => import('./pages/admin/Users'));
const Devices = lazy(() => import('./pages/admin/Devices'));
const SurveillancePolicies = lazy(() => import('./pages/admin/SurveillancePolicies'));
const Radio = lazy(() => import('./pages/admin/Radio'));
const Profile = lazy(() => import('./pages/Profile'));
const OwnerUsers = lazy(() => import('./pages/owner/OwnerUsers'));
const OwnerOrganizations = lazy(() => import('./pages/owner/OwnerOrganizations'));
const OwnerSystemHealth = lazy(() => import('./pages/owner/OwnerSystemHealth'));
const OwnerAuditLog = lazy(() => import('./pages/owner/OwnerAuditLog'));
const OwnerAcousticDomains = lazy(() => import('./pages/owner/OwnerAcousticDomains'));

function LoginFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-accent border-t-transparent" />
    </div>
  );
}

function PageSuspense({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: ReactNode;
}) {
  return <Suspense fallback={fallback}>{children}</Suspense>;
}

function WorkspaceHomeRedirect() {
  const userRole = useAuthStore((state) => state.user?.role ?? null);
  const isOwner = useAuthStore((state) => state.isOwner);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const destination = getConsoleLandingPath({
    userRole,
    isOwner,
    isOrganizationScope: !isOwner || isImpersonating,
    enableLabRoutes: labRoutesEnabled,
  });
  return <Navigate to={destination} replace />;
}

function OrganizationScopeOutlet() {
  const isOwner = useAuthStore((state) => state.isOwner);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  if (isOwner && !isImpersonating) {
    return <Navigate to={consoleRoutePaths['owner-organizations']} replace />;
  }
  return <Outlet />;
}

function PlatformScopeOutlet() {
  const isOwner = useAuthStore((state) => state.isOwner);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  if (isOwner && isImpersonating) {
    return <Navigate to={consoleRoutePaths.devices} replace />;
  }
  return <Outlet />;
}

const adminRoles = ['ADMIN', 'SUPERADMIN'] as const;

export default function AppRoutes() {
  return (
    <Routes>
      <Route
        path={consoleRoutePatterns.login}
        element={
          <PageSuspense fallback={<LoginFallback />}>
            <Login />
          </PageSuspense>
        }
      />

      <Route
        path={consoleRoutePaths['browser-mic-stream']}
        element={
          <ProtectedRoute>
            <LabRouteGuard>
              <PageSuspense fallback={<MonitoringSkeleton />}>
                <BrowserMicStream />
              </PageSuspense>
            </LabRouteGuard>
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <ConsoleShell />
          </ProtectedRoute>
        }
      >
        <Route element={<OrganizationScopeOutlet />}>
          <Route
            path={consoleRoutePaths.control}
            element={
              <PageSuspense fallback={<MonitoringSkeleton />}>
                <Control />
              </PageSuspense>
            }
          />
          <Route
            path={consoleRoutePatterns.deviceWorkspace}
            element={
              <PageSuspense fallback={<MonitoringSkeleton />}>
                <DeviceWorkspace />
              </PageSuspense>
            }
          />
          <Route
            path={consoleRoutePaths.investigations}
            element={
              <PageSuspense fallback={<MonitoringSkeleton />}>
                <DeviceWorkspace />
              </PageSuspense>
            }
          />
          <Route
            path={consoleRoutePaths.onboard}
            element={
              <RoleGuard roles={[...adminRoles]} redirectTo={consoleRoutePaths.devices}>
                <PageSuspense fallback={<MonitoringSkeleton />}>
                  <Onboard />
                </PageSuspense>
              </RoleGuard>
            }
          />
          <Route
            path={consoleRoutePaths['file-detection']}
            element={
              <LabRouteGuard>
                <RoleGuard roles={[...adminRoles]} redirectTo={consoleRoutePaths.devices}>
                  <PageSuspense fallback={<MonitoringSkeleton />}>
                    <AdvancedFileDetection />
                  </PageSuspense>
                </RoleGuard>
              </LabRouteGuard>
            }
          />
          <Route
            path={consoleRoutePaths.models}
            element={
              <LabRouteGuard>
                <RoleGuard roles={[...adminRoles]} redirectTo={consoleRoutePaths.devices}>
                  <PageSuspense fallback={<ModelsSkeleton />}>
                    <Models />
                  </PageSuspense>
                </RoleGuard>
              </LabRouteGuard>
            }
          />
          <Route
            path={consoleRoutePaths.training}
            element={
              <LabRouteGuard>
                <RoleGuard roles={[...adminRoles]} redirectTo={consoleRoutePaths.devices}>
                  <PageSuspense fallback={<TrainingSkeleton />}>
                    <Training />
                  </PageSuspense>
                </RoleGuard>
              </LabRouteGuard>
            }
          />
          <Route
            path={consoleRoutePatterns.trainingSummary}
            element={
              <LabRouteGuard>
                <RoleGuard roles={[...adminRoles]} redirectTo={consoleRoutePaths.devices}>
                  <PageSuspense fallback={<TrainingSkeleton />}>
                    <TrainingSummary />
                  </PageSuspense>
                </RoleGuard>
              </LabRouteGuard>
            }
          />
          <Route
            path={consoleRoutePaths.users}
            element={
              <RoleGuard roles={[...adminRoles]}>
                <PageSuspense fallback={<DashboardSkeleton />}>
                  <Users />
                </PageSuspense>
              </RoleGuard>
            }
          />
          <Route
            path={consoleRoutePaths.devices}
            element={
              <PageSuspense fallback={<DashboardSkeleton />}>
                <Devices />
              </PageSuspense>
            }
          />
          <Route
            path={consoleRoutePaths.radio}
            element={
              <LabRouteGuard>
                <RoleGuard roles={[...adminRoles]} redirectTo={consoleRoutePaths.devices}>
                  <PageSuspense fallback={<DashboardSkeleton />}>
                    <Radio />
                  </PageSuspense>
                </RoleGuard>
              </LabRouteGuard>
            }
          />
          <Route
            path={consoleRoutePaths['surveillance-policies']}
            element={
              <RoleGuard roles={[...adminRoles]}>
                <PageSuspense fallback={<DashboardSkeleton />}>
                  <SurveillancePolicies />
                </PageSuspense>
              </RoleGuard>
            }
          />
        </Route>

        <Route
          path={consoleRoutePaths.profile}
          element={
            <PageSuspense fallback={<DashboardSkeleton />}>
              <Profile />
            </PageSuspense>
          }
        />

        <Route
          path={consoleRoutePaths['reset-usb']}
          element={
            <LabRouteGuard requireOrganizationScope={false}>
              <PageSuspense fallback={<DashboardSkeleton />}>
                <ResetUsb />
              </PageSuspense>
            </LabRouteGuard>
          }
        />

        <Route element={<PlatformScopeOutlet />}>
          <Route
            path={consoleRoutePatterns.ownerRoot}
            element={
              <RoleGuard roles={['SUPERADMIN']}>
                <Outlet />
              </RoleGuard>
            }
          >
            <Route
              index
              element={<Navigate to={consoleRoutePatterns.ownerOrganizationsChild} replace />}
            />
            <Route
              path={consoleRoutePatterns.ownerUsersChild}
              element={
                <PageSuspense fallback={<DashboardSkeleton />}>
                  <OwnerUsers />
                </PageSuspense>
              }
            />
            <Route
              path={consoleRoutePatterns.ownerOrganizationsChild}
              element={
                <PageSuspense fallback={<DashboardSkeleton />}>
                  <OwnerOrganizations />
                </PageSuspense>
              }
            />
            <Route
              path={consoleRoutePatterns.ownerSystemHealthChild}
              element={
                <PageSuspense fallback={<DashboardSkeleton />}>
                  <OwnerSystemHealth />
                </PageSuspense>
              }
            />
            <Route
              path={consoleRoutePatterns.ownerAcousticDomainsChild}
              element={
                <PageSuspense fallback={<DashboardSkeleton />}>
                  <OwnerAcousticDomains />
                </PageSuspense>
              }
            />
            <Route
              path={consoleRoutePatterns.ownerAuditLogChild}
              element={
                <PageSuspense fallback={<DashboardSkeleton />}>
                  <OwnerAuditLog />
                </PageSuspense>
              }
            />
          </Route>
        </Route>
      </Route>

      <Route
        path={consoleRoutePatterns.root}
        element={
          <ProtectedRoute>
            <WorkspaceHomeRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path={consoleRoutePatterns.catchAll}
        element={
          <ProtectedRoute>
            <WorkspaceHomeRedirect />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
